import axios from 'axios'
import useAuthStore from '../store/authStore'
import { resolveApiBase } from '../utils/apiBase'

const API_URL = resolveApiBase()

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
})

/** Plain client for refresh — avoids running the same 401 interceptor recursively. */
const refreshClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor: prefer in-memory token (fresh right after silent refresh) then persist
api.interceptors.request.use(
  (config) => {
    // Default instance Content-Type is application/json — breaks multipart uploads (422 "Field required")
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers
      if (h.delete) {
        h.delete('Content-Type')
      } else if (h) {
        delete h['Content-Type']
      }
    }

    let bearer = useAuthStore.getState().token
    if (!bearer) {
      const raw = localStorage.getItem('auth-storage')
      if (raw) {
        try {
          const authData = JSON.parse(raw)
          bearer = authData.state?.token
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
    if (bearer) {
      config.headers.Authorization = `Bearer ${bearer}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response: on 401, try refresh once then retry; otherwise match prior logout behavior
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const status = error.response?.status
    const requestUrl = String(originalRequest?.url || '')

    if (status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    const isAuthFlowRequest =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/check-username') ||
      requestUrl.includes('/auth/check-email')

    if (isAuthFlowRequest) {
      return Promise.reject(error)
    }

    if (requestUrl.includes('/auth/refresh')) {
      useAuthStore.getState().clearAuth()
      if (
        window.location.pathname !== '/login' &&
        window.location.pathname !== '/register'
      ) {
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    if (!originalRequest._retry) {
      originalRequest._retry = true
      const authRaw = localStorage.getItem('auth-storage')
      if (authRaw) {
        try {
          const parsed = JSON.parse(authRaw)
          const refreshToken = parsed.state?.refreshToken
          if (refreshToken) {
            const { data } = await refreshClient.post('/auth/refresh', {
              refresh_token: refreshToken,
            })
            useAuthStore.getState().updateTokens(data.access_token, data.refresh_token)
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${data.access_token}`
            return api(originalRequest)
          }
        } catch {
          // fall through to logout
        }
      }
    }

    useAuthStore.getState().clearAuth()
    const isPublicAuthPage =
      window.location.pathname === '/login' ||
      window.location.pathname === '/register'
    if (!isPublicAuthPage) {
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default api
